import React, { useCallback, useEffect, useRef, useState } from 'react';
import { runnerApi, downloadBlob, errorText, type Batch, type BatchRollout, type BatchStep, type RingStatus } from '../../../api/migrationRunner';
import { Btn, BATCH_TONE, ErrorBox, Panel, Pill, STEP_LABEL, STEP_TONE, Spinner, duration, shortName, when } from './ui';

interface Props {
  batchId: string;
  onOpenRun: (rowId: number) => void;
  onOpenBatch: (id: string) => void;
  onChanged?: () => void;
}

const DOT: Record<string, string> = {
  success: 'bg-green-600', running: 'bg-blue-600', pending: 'bg-slate-300', skipped: 'bg-amber-500',
  failed: 'bg-red-600', partial: 'bg-amber-500', locked: 'bg-red-600', blocked: 'bg-red-600',
  not_run: 'bg-slate-300', cancelled: 'bg-slate-300', interrupted: 'bg-amber-500',
};

function stepDetail(s: BatchStep): string {
  if (s.status === 'running') return s.statementCount ? `${s.statementCount} statement${s.statementCount === 1 ? '' : 's'}` : 'running';
  if (s.status === 'skipped') {
    if (s.reason === 'requires') return `waiting on another migration: ${s.error || 'a required folder is not applied here yet'}`;
    return s.reason === 'no_database' ? 'skipped: no database' : s.reason === 'already_applied' ? 'already applied' : 'skipped';
  }
  if (s.status === 'success') {
    const n = s.applied ?? s.statementCount;
    return `${n ?? '?'} statement${n === 1 ? '' : 's'}${s.fromIndex ? ` (from ${s.fromIndex + 1})` : ''} · ${duration(s.durationMs)}`;
  }
  if (['failed', 'partial', 'locked', 'blocked', 'interrupted'].includes(s.status)) {
    const where = s.failedIndex !== null && s.failedIndex !== undefined ? `statement ${s.failedIndex + 1}${s.statementCount ? ` of ${s.statementCount}` : ''}: ` : '';
    return `${where}${s.error || s.status}`;
  }
  if (s.status === 'not_run' && s.reason === 'stopped_after_failure') return 'not run: the batch stopped at a failure';
  return STEP_LABEL[s.status];
}

const RING_DOT: Record<RingStatus, string> = {
  passed: 'bg-green-600', running: 'bg-blue-600', waiting: 'bg-slate-300', paused: 'bg-amber-500', failed: 'bg-red-600', not_run: 'bg-slate-300',
};
const RING_TONE: Record<RingStatus, 'ok' | 'info' | 'mut' | 'warn' | 'bad'> = {
  passed: 'ok', running: 'info', waiting: 'mut', paused: 'warn', failed: 'bad', not_run: 'mut',
};

function ringNote(r: BatchRollout['rings'][number], rollout: BatchRollout, i: number, steps: BatchStep[]): string {
  if (r.status === 'passed') return r.finishedAt ? `passed ${when(r.finishedAt)}` : 'passed';
  if (r.status === 'failed') return `${Object.entries(r.counts).filter(([k]) => ['failed', 'partial', 'locked', 'blocked', 'interrupted'].includes(k)).reduce((n, [, v]) => n + (v || 0), 0)} need attention`;
  if (r.status === 'running') {
    const cur = steps.find((s) => s.ring === i && s.status === 'running');
    return cur ? `running · ${cur.companyName}` : 'running';
  }
  if (r.status === 'paused' && rollout.paused) {
    return rollout.paused.reason === 'wait' && rollout.paused.resumeAfter ? `starts by itself at ${when(rollout.paused.resumeAfter)}` : 'paused: the step before failed';
  }
  if (r.status === 'not_run') return 'not run';
  return i === 0 ? 'starts first' : `starts after step ${i} passes`;
}

/** The mockup's "Apply … in steps" timeline. */
const RolloutPanel: React.FC<{ batch: Batch; busy: boolean; onContinue: () => void }> = ({ batch, busy, onContinue }) => {
  const rollout = batch.rollout!;
  const steps = batch.steps || [];
  const current = rollout.paused ? rollout.paused.ring : Math.max(0, rollout.rings.findIndex((r) => r.status === 'running' || r.status === 'waiting'));
  return (
    <Panel title="In steps" actions={<Pill tone="info">Step {Math.min(current + 1, rollout.rings.length)} of {rollout.rings.length}</Pill>}>
      <div className="space-y-2">
        {rollout.rings.map((r, i) => (
          <div key={i} className="grid grid-cols-[14px_1fr_auto] items-center gap-3 text-[13px]">
            <span className={`h-2.5 w-2.5 rounded-full ${RING_DOT[r.status]} ${r.status === 'running' ? 'animate-pulse' : ''}`} />
            <span className="min-w-0">
              <b className="text-slate-900">Step {i + 1}{r.label ? ` · ${r.label}` : ''}</b>{' '}
              <span className="text-slate-500">
                {r.targets.length <= 2 ? r.targets.map((t) => steps.find((s) => s.targetDb === t)?.companyName || t).join(', ') : `${r.targets.length} companies`}
              </span>
            </span>
            <Pill tone={RING_TONE[r.status]}>{ringNote(r, rollout, i, steps)}</Pill>
          </div>
        ))}
        <div className="text-xs text-slate-500">
          {rollout.pauseOnFailure ? 'Pauses automatically if a step fails' : 'Does not pause on failure'}
          {rollout.waitMinutes ? ` · waits ${rollout.waitMinutes} min between steps` : ''}
          {rollout.events.length > 0 && ` · last continued by ${rollout.events[rollout.events.length - 1].continuedBy}`}
        </div>
        {batch.status === 'paused' && rollout.paused && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border-[1.5px] border-amber-400 bg-amber-50 px-3 py-2 text-[13px]">
            <span className="min-w-0 flex-1 text-slate-700">
              {rollout.paused.reason === 'failure'
                ? <>Step {rollout.paused.ring} had a failure, so step {rollout.paused.ring + 1} hasn't started. Open the failed run below, fix or resolve it, then continue.</>
                : <>Waiting between steps. Step {rollout.paused.ring + 1} starts by itself{rollout.paused.resumeAfter ? ` at ${when(rollout.paused.resumeAfter)}` : ''}, or now.</>}
            </span>
            <Btn small disabled={busy} onClick={onContinue}>{rollout.paused.reason === 'failure' ? `Continue with step ${rollout.paused.ring + 1}` : 'Start next step now'}</Btn>
          </div>
        )}
      </div>
    </Panel>
  );
};

/**
 * A batch's progress, polled from the server (the batch lives in the
 * registry, so closing this page or landing on another server is fine).
 */
const BatchView: React.FC<Props> = ({ batchId, onOpenRun, onOpenBatch, onChanged }) => {
  const [batch, setBatch] = useState<Batch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const lastStatus = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      const b = await runnerApi.batch(batchId);
      setBatch(b);
      setError(null);
      if (lastStatus.current && lastStatus.current !== b.status) onChanged?.();
      lastStatus.current = b.status;
      return b;
    } catch (err) {
      setError(errorText(err, 'Could not load the batch'));
      return null;
    }
  }, [batchId, onChanged]);

  useEffect(() => {
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      const b = await load();
      if (stop) return;
      if (!b || b.status === 'queued' || b.status === 'running') timer = setTimeout(tick, 2000);
      // Paused between rollout steps: a wait can run out on the server.
      else if (b.status === 'paused') timer = setTimeout(tick, 15000);
    };
    tick();
    return () => { stop = true; clearTimeout(timer); };
  }, [load]);

  const act = async (name: string, fn: () => Promise<unknown>) => {
    setBusy(name);
    try { await fn(); await load(); } catch (err) { setError(errorText(err)); } finally { setBusy(null); }
  };

  if (!batch) {
    return error ? <ErrorBox>{error}</ErrorBox> : <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner /> Loading batch…</div>;
  }

  const steps = batch.steps || [];
  const done = steps.filter((s) => !['pending', 'running'].includes(s.status)).length;
  const pct = steps.length ? Math.round((done / steps.length) * 100) : 100;
  const live = batch.status === 'queued' || batch.status === 'running';
  const paused = batch.status === 'paused';
  const failures = steps.filter((s) => ['failed', 'partial', 'locked', 'blocked', 'interrupted'].includes(s.status)).length;
  const counts = batch.summary.counts;

  return (
    <div className="space-y-4">
      <Panel
        title={batch.options.label || `${batch.summary.migrations} migration${batch.summary.migrations === 1 ? '' : 's'} on ${batch.summary.companies} database${batch.summary.companies === 1 ? '' : 's'}`}
        actions={<Pill tone={BATCH_TONE[batch.status] ?? 'mut'}>{batch.status}{batch.cancelRequested && live ? ' · cancelling' : ''}</Pill>}
      >
        <div className="space-y-3">
          <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
            <div className={`h-full ${failures ? 'bg-amber-500' : 'bg-blue-600'} transition-all`} style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[13px] text-slate-600">
            <b className="text-slate-900">{done} of {steps.length} steps done</b>
            {counts.running ? ` · ${counts.running} running` : ''}
            {counts.pending ? ` · ${counts.pending} waiting` : ''}
            {counts.skipped ? ` · ${counts.skipped} skipped` : ''}
            {failures ? ` · ${failures} need attention` : ''}
            {' · '}started {when(batch.startedAt || batch.createdAt)} by {batch.createdBy}
            {live && ' · safe to close this page'}
          </div>
          <div className="text-xs text-slate-500">
            {batch.options.order === 'tenant_first' ? 'One company at a time' : 'One migration at a time'} ·{' '}
            {batch.options.stopOnFailure ? 'stops at the first failure' : 'keeps going after failures'} ·{' '}
            {batch.options.backup ? 'backs up each database first' : 'no backups'}
            {batch.options.allowNonIdempotent ? ' · non-idempotent override on' : ''}
          </div>
          <div className="flex flex-wrap gap-2">
            {paused && (
              <Btn kind="ghost" small disabled={!!busy} onClick={() => act('cancel', () => runnerApi.cancelBatch(batch.id))}>
                Cancel the remaining steps
              </Btn>
            )}
            {live && (
              <>
                <Btn kind="ghost" small disabled={!!busy || batch.cancelRequested >= 1} onClick={() => act('cancel', () => runnerApi.cancelBatch(batch.id, 'after_target'))}>
                  Cancel after this company
                </Btn>
                <Btn kind="ghost" small disabled={!!busy || batch.cancelRequested >= 2} onClick={() => act('stop', () => runnerApi.cancelBatch(batch.id, 'now'))}
                  title="Stops between statements: the company in progress is left partly applied">
                  Stop now
                </Btn>
              </>
            )}
            <Btn kind="ghost" small disabled={!!busy} onClick={() => act('log', async () => downloadBlob(await runnerApi.batchLog(batch.id), `migration-batch-${batch.id}.log`))}>
              Export log
            </Btn>
            {!live && !paused && failures > 0 && (
              <Btn small disabled={!!busy} onClick={() => act('retry', async () => onOpenBatch((await runnerApi.retryBatch(batch.id)).id))}
                title="A new batch with only the failed steps; partly applied ones resume where they stopped">
                Retry failed ({failures})
              </Btn>
            )}
          </div>
          {batch.error && !live && <ErrorBox>{batch.error}</ErrorBox>}
          {error && <ErrorBox>{error}</ErrorBox>}
        </div>
      </Panel>

      {batch.rollout && (
        <RolloutPanel batch={batch} busy={!!busy} onContinue={() => act('continue', async () => { await runnerApi.continueBatch(batch.id); onChanged?.(); })} />
      )}

      <Panel title="Log" bodyClass="divide-y divide-slate-100">
        {steps.map((s) => (
          <div key={s.i} className="grid grid-cols-[14px_1fr_auto] items-start gap-3 px-4 py-2.5 text-[13px]">
            <span className={`mt-1.5 h-2.5 w-2.5 rounded-full ${DOT[s.status] ?? 'bg-slate-300'} ${s.status === 'running' ? 'animate-pulse' : ''}`} />
            <div className="min-w-0">
              <b className="text-slate-900">{s.companyName}</b>{' '}
              <span className="text-slate-500">{shortName(s.migrationName)}</span>
              <div className={`mt-0.5 break-words text-xs ${['failed', 'partial', 'locked', 'blocked'].includes(s.status) ? 'text-red-700' : 'text-slate-500'}`}>
                {stepDetail(s)}
                {s.backupWarning && <span className="ml-1 text-amber-700">({s.backupWarning})</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {s.runRowId && s.status !== 'success' && (
                <Btn kind="ghost" small onClick={() => onOpenRun(s.runRowId!)}>Open</Btn>
              )}
              <Pill tone={STEP_TONE[s.status]}>{STEP_LABEL[s.status]}</Pill>
            </div>
          </div>
        ))}
        {steps.length === 0 && <div className="px-4 py-6 text-sm text-slate-500">No steps.</div>}
      </Panel>
    </div>
  );
};

export default BatchView;
